import {
  Button,
  ButtonProps,
  Icon,
  SxProps,
  styled,
  visuallyHidden,
} from "@outschool/backpack";
import { faShare } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  Modal,
  TrackingProperties,
  useImpressionTracking,
} from "@outschool/ui-legacy-component-library";
import { Screen, useNodeRef } from "@outschool/ui-utils";
import React from "react";

export type ShareMenuProps = React.PropsWithChildren<
  Omit<ShareButtonProps, "sx" | "onClick"> & {
    buttonSx?: ShareButtonProps["sx"];
  }
>;

export default function ShareMenu({
  children,
  variant,
  size,
  label,
  hideLabelOnMobile,
  simpleShare,
  linkColor,
  trackingMetaData,
  showIcon,
  buttonSx,
}: ShareMenuProps) {
  const [isShareBoxVisible, setIsSharedBoxvisable] = React.useState(false);

  const toggleShare = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      setIsSharedBoxvisable(oldValue => !oldValue);
    },
    []
  );

  const closeShare = React.useCallback(() => {
    setIsSharedBoxvisable(false);
  }, []);

  return (
    <React.Fragment>
      <ShareButton
        simpleShare={simpleShare}
        variant={variant}
        size={size}
        label={label}
        hideLabelOnMobile={hideLabelOnMobile}
        onClick={toggleShare}
        linkColor={linkColor}
        trackingMetaData={trackingMetaData}
        showIcon={showIcon}
        sx={buttonSx}
      />
      <Modal fullBleed={false} open={isShareBoxVisible} onClose={closeShare}>
        {children}
      </Modal>
    </React.Fragment>
  );
}

interface ShareButtonProps {
  onClick: (e: React.SyntheticEvent<Element, Event>) => void;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  simpleShare?: boolean;
  label?: string;
  hideLabelOnMobile?: boolean;
  linkColor?: string;
  showIcon?: boolean;
  trackingMetaData?: TrackingProperties;
  sx?: SxProps;
}

const StyledSpan = styled("span")({});

export function ShareButton({
  variant,
  size,
  onClick,
  simpleShare,
  label,
  hideLabelOnMobile = false,
  linkColor = "inherit",
  showIcon = true,
  trackingMetaData,
  sx = {},
}: ShareButtonProps) {
  const { t } = useTranslation("ui-components-website\\ShareMenu");
  if (!label) {
    label = t`Share`;
  }
  const isMobile = Screen.useIsMobile();
  const [shareNode, shareRef] = useNodeRef();
  const { trackTouch: trackShareTouch } = useImpressionTracking({
    node: shareNode,
    trackingLabel: "share_button",
    uniqueId: "share_button",
    sharedProperties: trackingMetaData,
  });

  return simpleShare ? (
    <StyledSpan
      sx={{ cursor: "pointer", ...sx }}
      onClick={(e: any) => {
        e.preventDefault();
        trackShareTouch();
        onClick(e);
      }}
      onKeyDown={(e: any) => {
        // strangely, e.code is null
        if (e.keyCode === 13) {
          e.preventDefault();
          trackShareTouch();
          onClick(e);
        }
      }}
      tabIndex={0}
      role="link"
    >
      <Icon ref={shareRef} icon={faShare} sx={{ color: "primary.700" }} />
      <span style={visuallyHidden}>{label}</span>
    </StyledSpan>
  ) : (
    <Button
      variant={variant}
      sx={[
        {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      ref={shareRef}
      size={size}
      onClick={(e: any) => {
        trackShareTouch();
        onClick(e);
      }}
    >
      {showIcon && (
        <Icon
          sx={{
            color: linkColor,
          }}
          icon={faShare}
          data-test-id="share-menu-icon"
        />
      )}
      {!(hideLabelOnMobile && isMobile) ? (
        <React.Fragment>&ensp;{label}</React.Fragment>
      ) : (
        <span style={visuallyHidden}>{label}</span>
      )}
    </Button>
  );
}
